import { Component, forwardRef, HostBinding, inject, input, model, OnInit, signal, viewChild } from '@angular/core';
import { InventoryActionCreateFormComponent } from '../inventory-action-create-form.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InventoryAction, InventoryActionForStock } from '../../../../../models/inventory_action.model';
import { catchError, debounceTime, filter, of, switchMap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InventoryActionFormComponent } from '../../../../../shared/common-forms/inventory-action-form/inventory-action-form.component';
import { CameraService } from '../../../../../shared/image-uploader/camera/camera.service';
import { getDownloadURL } from 'firebase/storage';
import { toInventoryActionProductForm } from '../../../../../models/inventory-action-product';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { HumanPipe } from '../../../../../shared/pipes/human.pipe';
import { FormGroupPipe } from '../../../../../shared/pipes/form-group.pipe';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { INVOICE_TYPES } from '../../../../../models/invoice.model';
import { AdminService } from '../../../../../admin/services/admin.service';

@Component({
    selector: 'app-stock-create-form',
    templateUrl: './stock-create-form.component.html',
    styleUrls: ['./stock-create-form.component.scss', '../inventory-action-create-form.component.scss'],
    imports: [
      AsyncPipe,
      HumanPipe,
      FormGroupPipe,
      FormsModule,
      forwardRef(() => InventoryActionFormComponent),
      MatAutocompleteModule,
      MatButtonModule,
      MatDatepickerModule,
      MatDialogModule,
      MatFormFieldModule,
      MatIconModule,
      MatInputModule,
      MatSelectModule,
      MatProgressSpinnerModule,
      MatToolbarModule,
      NgClass,
      ReactiveFormsModule,
    ]
})
export class StockCreateFormComponent extends InventoryActionCreateFormComponent implements OnInit {
  isReadOnly = input<any>(false);

  sourceDocument = input(null);

  form: FormGroup = this.fb.group({
    id: this.fb.control(null),
    inventory_action_products: this.fb.array([]),
    warehouse_id: [null, [Validators.required]],
    status: this.fb.control(null),
    invoice: this.fb.group({
      due_date: this.fb.control(null, Validators.required),
      invoice_type: this.fb.control(null, Validators.required),
      url: this.fb.control(null, Validators.required),
      stakeholder_id: this.fb.control(null, Validators.required),
      stakeholder_type: this.fb.control('Provider'),
    })
  });
  
  inventoryActionFormComponent = viewChild(InventoryActionFormComponent);

  invoiceTypes = INVOICE_TYPES;
  
  providers = this.providerService.providers;

  // Used when source document is given. For example, from work's 'create document' plugin
  selectedSourceDocument = signal<InventoryAction | null>(null);

  selectedSourceDocumentType = model<string>('Invoice::Purchase');

  sourceDocuments = signal<InventoryAction[]>([]);

  SOURCE_DOCUMENT_TYPES = ['InventoryAction::BuyOrder', 'InventoryAction::Movement', 'Invoice::Purchase'];

  
  private readonly cameraService = inject(CameraService);


  @HostBinding('class') background;


  get isInvoiceMode(): boolean {
    return this.selectedSourceDocumentType() === 'Invoice::Purchase';
  }

  get isMovementMode(): boolean {
    return this.selectedSourceDocumentType() === 'InventoryAction::Movement';
  }

  get showTotals(): boolean {
    return !this.isMovementMode;
  }

  ngOnInit(): void {
    super.ngOnInit();

    if (this.isPluginMode()) {
      this.cdr.markForCheck();
      this.background = 'plugin-mode';
      this.selectedSourceDocument.set(this.sourceDocument());
      this.selectedSourceDocumentType.set(this.sourceDocument()?.type);
      this.loadSourceDocument(this.selectedSourceDocument()?.id);
    }
  }

  

  onSourceTypeChange() {
    this.form.get('source_document_id').setValue(null);
    this.sourceDocuments.set([]);
    this.setFormControlValidators();
  }

  protected setFormControlValidators() {
    super.setFormControlValidators();

    if (this.isReadOnly()) {
      (this.form.get('warehouse_id') as FormControl).disable();
      (this.form.get('invoice.due_date') as FormControl).disable();
      (this.form.get('invoice.url') as FormControl).disable();
      (this.form.get('invoice.stakeholder_id') as FormControl).disable();
    }

    if (this.isInvoiceMode) {
      (this.form.get('warehouse_id') as FormControl).enable();
      (this.form.get('source_document_id') as FormControl)?.clearValidators();
      (this.form.get('source_document_id') as FormControl)?.updateValueAndValidity();
      (this.form.get('invoice.stakeholder_id') as FormControl).enable()
    } else {
      (this.form.get('warehouse_id') as FormControl).disable();
      (this.form.get('source_document_id') as FormControl)?.setValidators([Validators.required]);
      (this.form.get('invoice.stakeholder_id') as FormControl).disable();
    }

    if (this.isMovementMode) {
      (this.form.get('invoice.stakeholder_id') as FormControl).clearValidators();
      (this.form.get('invoice.due_date') as FormControl).clearValidators();
      (this.form.get('invoice.url') as FormControl).clearValidators();
      (this.form.get('invoice.stakeholder_id') as FormControl).updateValueAndValidity();
      (this.form.get('invoice.due_date') as FormControl).updateValueAndValidity();
      (this.form.get('invoice.url') as FormControl).updateValueAndValidity();
    } else {
      (this.form.get('invoice.stakeholder_id') as FormControl).setValidators([Validators.required]);
      (this.form.get('invoice.due_date') as FormControl).setValidators([Validators.required]);
      (this.form.get('invoice.url') as FormControl).setValidators([Validators.required]);
      (this.form.get('invoice.stakeholder_id') as FormControl).updateValueAndValidity();
      (this.form.get('invoice.due_date') as FormControl).updateValueAndValidity();
      (this.form.get('invoice.url') as FormControl).updateValueAndValidity();
    }
  }

  protected setFormHandlers() {

    super.setFormHandlers();

    if (this.isPluginMode()) return;

    this.form.addControl('source_document_id', this.fb.control(null, Validators.required));
    this.form.get('source_document_id').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      filter((value) => {
        if (!value) {
          this.selectedSourceDocument.set(null);
          return false;
        }

        if (typeof value === 'string') {
          this.selectedSourceDocument.set(null);
          return true;
        }

        this.loadSourceDocument(value.id);
        return false;
      }),
      switchMap((query) => {
        return this.inventoryActionService.search({
          type: this.selectedSourceDocumentType(),
          draft: 'false',
          completed_at: 'null',
          query,
        });
      }),
      catchError((error) => {
        console.error(`Error searching ${this.selectedSourceDocumentType()}:`, error);

        return of(new HttpResponse({ body: [] }));
      }),
    ).subscribe((sourceDocuments) => {
      if (!sourceDocuments.body) {
        return;
      }

      this.sourceDocuments.set(sourceDocuments.body);
    });
  }

  private loadSourceDocument(id: number): void {
    this.loading.set(true);
    this.inventoryActionService.get(id).subscribe((selectedDocument) => {
      this.selectedSourceDocument.set(selectedDocument);

      if (selectedDocument.type === "InventoryAction::Movement") {
        (this.form.get('warehouse_id') as FormControl).setValue(selectedDocument?.target_warehouse?.id);
      } else {
        (this.form.get('warehouse_id') as FormControl).setValue((selectedDocument as InventoryActionForStock)?.warehouse?.id);
        (this.form.get('invoice.stakeholder_id') as FormControl).setValue((selectedDocument as InventoryActionForStock)?.owner?.id);
        (this.form.get('invoice.due_date') as FormControl).setValue((selectedDocument as InventoryActionForStock)?.invoice?.due_date);
        (this.form.get('invoice.url') as FormControl).setValue((selectedDocument as InventoryActionForStock)?.invoice?.url);
      }
      (this.form.get('status') as FormControl).setValue({
        approval: selectedDocument.approval,
        approved: selectedDocument.approved,
        completed: selectedDocument.completed,
        failed: selectedDocument.failed,
        locked: selectedDocument.locked,
        finish: selectedDocument.finish,
      });

      let alreadyExistsStock = !!selectedDocument.stock;
      let sourceDocument: Partial<InventoryAction>;
      if (alreadyExistsStock) { // case where stock already exists (for example, when source is a buy order)
        sourceDocument = selectedDocument.stock;
        this.form.get("id").setValue(sourceDocument.id);
      } else {
        sourceDocument = selectedDocument;
        this.form.get("id").setValue(null);
      }

      this.form.controls.inventory_action_products = this.fb.array(
        sourceDocument.inventory_action_products.map(
          (product) =>  toInventoryActionProductForm(
            {
              ...product,
              id: alreadyExistsStock ? product.id: undefined,
            },
          ) 
        ),
      );

      this.loading.set(false);

      this.updated.emit();
    });
  };

  displayDocumentFn(inventoryAction: InventoryAction): string {
    if(!inventoryAction) return "";

    return inventoryAction?.correlative_id?.toString();
  }

  uploadInvoiceFile(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) {
      return;
    }

    this.loading.set(true);

    const uploadTask = this.cameraService.saveImage(file);
    uploadTask.on('state_changed', (snapshot:any) => {
    }, (error) => {
      console.error('Error uploading image', error);
      this.snackBar.open('Error al subir la imagen', 'Cerrar', { duration: 5000 });
    }, () => {
      getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
        (this.form.get('invoice.url') as FormControl).setValue(downloadURL);
        this.loading.set(false);
      });
    });
  }

  protected getFormDataForSending() {
    const formData = super.getFormDataForSending();

    formData.type = 'InventoryAction::Stock';
    formData.draft = false;

    if (!this.isInvoiceMode) {
      formData.document_type = 'InventoryAction';
      formData.document_id = this.selectedSourceDocument().id;
    }

    if (this.isPluginMode()) {
      formData.work_instance_id = this.workInstanceId();
    }

    if (!this.isMovementMode) {
      delete formData['invoice'];
      formData.invoice_attributes = (this.form.get('invoice') as FormControl).getRawValue();
      formData.invoice_attributes.type = 'Invoice::Purchase';
      formData.invoice_attributes.company_id = this.adminService.local$.getValue().id;
    }

    return formData;
  }
}
